import { createContext, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  clearSignOutInFlight,
  getSession,
  markSignOutInFlight,
  onAuthStateChange,
  readPersistedAuthSession,
  sessionsEqual,
  signOut,
  type Session,
} from '@pc/db';

interface AuthCtx {
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

const SESSION_BOOTSTRAP_TIMEOUT_MS = 4000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(() => readPersistedAuthSession());
  const initiallySeeded = session !== null;
  const [loading, setLoading] = useState(!initiallySeeded);
  const settledRef = useRef(initiallySeeded);

  useEffect(() => {
    let alive = true;

    const settle = (next: Session | null) => {
      if (!alive) return;
      setSession((prev) => (sessionsEqual(prev, next) ? prev : next));
      if (!settledRef.current) {
        settledRef.current = true;
        setLoading(false);
      }
    };

    const off = onAuthStateChange((_event, s) => {
      if (s && s.access_token) clearSignOutInFlight();
      settle(s);
    });

    const timeout = window.setTimeout(() => {
      if (!settledRef.current) {
        void getSession().then(settle).catch(() => settle(null));
      }
    }, SESSION_BOOTSTRAP_TIMEOUT_MS);

    return () => {
      alive = false;
      off();
      window.clearTimeout(timeout);
    };
  }, []);

  const value = useMemo<AuthCtx>(
    () => ({
      session,
      loading,
      signOut: async () => {
        markSignOutInFlight();
        await signOut();
        setSession(null);
        if (typeof window !== 'undefined') {
          window.location.replace(
            'https://auth.peptideclients.com/signout?return_to=' +
              encodeURIComponent('https://peptideclients.com/'),
          );
        }
      },
    }),
    [session, loading],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useAuth(): AuthCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>.');
  return v;
}
